'use client';

import React, { useState, useEffect } from 'react';
import { useTheme } from 'next-themes';
import { useLanguage } from '../context/LanguageContext';
import Image from 'next/image';
import { AnimatePresence, motion } from 'framer-motion';
import { getNavigation } from '@/config/navigation';
import { DesktopNav } from '@/components/layout/DesktopNav';
import { MobileMenu } from '@/components/layout/MobileMenu';
import { MobileBottomNav } from '@/components/layout/MobileBottomNav';
import { SunIcon, MoonIcon } from '@/components/layout/NavIcons';
import { EditableContent } from './EditableContent';

const Header = () => {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { t, language, setLanguage, isRTL } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const toggleTheme = () => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  const toggleLang = () => {
    const newLang = isRTL ? 'en' : 'ar';
    setLanguage(newLang);
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('language', newLang);
      } catch (e) {
        // storage unavailable (e.g. iOS "Block All Cookies") - safe to ignore
      }
    }
  };

  // Use a safe default for server-side rendering
  const logoSrc = mounted && (resolvedTheme || theme) === 'dark' ? '/images/logo-dark.png' : '/images/logo-light.png';

  const tabs = getNavigation(language, toggleLang, t);

  return (
    <>
      <header className={`sticky top-0 z-50 bg-white/80 dark:bg-black/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700  ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="container mx-auto  flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="relative h-16 md:h-20 w-32 md:w-40">
            <Image src={logoSrc} alt="Logo" fill className="object-contain" priority sizes="(max-width: 768px) 128px, 160px" />
          </a>

          {/* Desktop Tabs */}
          <div className={`hidden md:flex items-center`}>
            <DesktopNav tabs={tabs} isRTL={isRTL} />
          </div>

          {/* Theme & Get Started */}
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Toggle theme"
            >
              {mounted && theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            </button>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto"
            >
              <a
                href="#get-started"
                className="inline-flex items-center justify-center rounded-md bg-[#D6213B] text-white px-4 py-2 text-sm font-medium hover:bg-[#D6213B]/80 transition-colors red-glow hover:red-glow-lg"
              >
                <EditableContent id="hero.cta.primary" showEditIcon={true} />
              </a>
            </motion.p>
          </div>

          {/* Mobile Theme Toggle (Hamburger removed, moved to bottom nav) */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 "
              aria-label="Toggle theme"
            >
              {mounted && theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Drawer */}
        <AnimatePresence>
          {mobileMenuOpen && <MobileMenu tabs={tabs} onClose={() => setMobileMenuOpen(false)} />}
        </AnimatePresence>
      </header>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav onMenuClick={() => setMobileMenuOpen(true)} isRTL={isRTL} />
    </>
  );
};

export default Header;
